import { useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, Link, useNavigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import LoginPage from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Users from "@/pages/Users";
import UserDetail from "@/pages/UserDetail";
import History from "@/pages/History";
import GeoRequests from "@/pages/GeoRequests";
import EndpointEvents from "@/pages/EndpointEvents";
import EndpointMonitor from "@/pages/EndpointMonitor";
import EmailTool from "@/pages/EmailTool";
import "@/App.css";

function Protected({ children, adminOnly }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-zinc-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/login" replace />;
  return children;
}

function Shell({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const items = [
    { path: "/", label: "Overview" },
    { path: "/users", label: "Users" },
    { path: "/geo-requests", label: "Geo Requests" },
    { path: "/endpoint-monitor", label: "Endpoint Monitor" },
    { path: "/endpoint-events", label: "Legacy Events" },
    { path: "/email-tool", label: "Email Tool" },
    { path: "/history", label: "URL History" },
  ];
  return (
    <div className="min-h-screen bg-[#0b0c10] text-zinc-100 flex">
      <aside className="w-64 border-r border-zinc-800 bg-[#0e0f14] p-5 flex flex-col">
        <div className="mb-8 flex items-center gap-3">
          <img src="/logo.png" alt="" className="w-10 h-10 rounded-lg shadow-[0_4px_16px_rgba(139,92,246,0.35)]" />
          <div>
            <div className="text-[11px] tracking-[0.3em] text-emerald-400/80">SND</div>
            <div className="text-base font-semibold tracking-tight leading-tight">Secure Browser</div>
            <div className="text-[11px] text-zinc-500 mt-0.5">Admin Console</div>
          </div>
        </div>
        <nav className="flex flex-col gap-1">
          {items.map((it) => (
            <Link
              key={it.path}
              to={it.path}
              data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-3 py-2 rounded-md text-sm transition-colors ${
                loc.pathname === it.path
                  ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                  : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900"
              }`}
            >
              {it.label}
            </Link>
          ))}
        </nav>
        <div className="mt-auto pt-6 border-t border-zinc-800">
          <div className="text-xs text-zinc-500">Signed in as</div>
          <div className="text-sm truncate" data-testid="current-user-email">{user?.email}</div>
          <button
            data-testid="logout-btn"
            onClick={async () => { await logout(); nav("/login"); }}
            className="mt-3 w-full text-xs py-2 rounded-md bg-zinc-900 hover:bg-zinc-800 border border-zinc-800"
          >
            Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-auto">{children}</main>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/" element={<Protected adminOnly><Shell><Dashboard /></Shell></Protected>} />
          <Route path="/users" element={<Protected adminOnly><Shell><Users /></Shell></Protected>} />
          <Route path="/users/:id" element={<Protected adminOnly><Shell><UserDetail /></Shell></Protected>} />
          <Route path="/geo-requests" element={<Protected adminOnly><Shell><GeoRequests /></Shell></Protected>} />
          <Route path="/endpoint-events" element={<Protected adminOnly><Shell><EndpointEvents /></Shell></Protected>} />
          <Route path="/endpoint-monitor" element={<Protected adminOnly><Shell><EndpointMonitor /></Shell></Protected>} />
          <Route path="/email-tool" element={<Protected adminOnly><Shell><EmailTool /></Shell></Protected>} />
          <Route path="/history" element={<Protected adminOnly><Shell><History /></Shell></Protected>} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
